import React, { useEffect, useRef, useState } from 'react'

import { keyValidationSchema, editValidationSchema, NAME_LENGTH, SUMMARY_LENGTH } from './validation-schemas'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'

import lang from '@/lang'

import Topbar from '@/modules/base/components/topbar/topbar'
import { CancelButton, ButtonIconSquare } from '@/modules/base/components/buttons'
import { TextInput, TextareaInput, HtmlInput } from '@/modules/base/components/inputs/index'
import { Title } from '@/modules/base/components/texts'
import Loader from '@/modules/base/components/loader/loader'
import AppCard from '../card/app'
import FeatureLockedNotice from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-locked-notice'
import { FeatureTypes } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-config'
import { isPublicApiNotBookedError } from '../../util/public-api-error'
import ScopeSection from './sections/scopes'
import IconUpload from './sections/icon-upload'
import Screenshots from './sections/screenshots'

import { PATH_PUBLISHED } from '../apps-list'

import IconCheck from '@assets/images/icons/check.svg'

import { createApp, fetchDeveloperApp, persistApp, fetchDeveloper, publishApp, clearOnUnmount } from 'redux/ducks/apps'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { useNavigate } from 'react-router'

const AppForm = ({ initialAppId, initialVersion }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { editApp, loadingMessage } = useSelector(state => state.apps)
  const [newIcon, setNewIcon] = useState()
  const [newScreenshots, setNewScreenshots] = useState([])
  const [isEdit, setIsEdit] = useState(initialAppId && initialVersion)
  const [isLoading, setIsLoading] = useState(true)

  const { loading: accessLoading, hasPublicApiAccess } = useSelector(state => state.subscription.details)
  const readOnly = !hasPublicApiAccess
  const hadPublicApiAccess = useRef(null)

  const classes = useStyles({ isEdit, isLoading: isLoading || accessLoading }, [editApp.appId, isLoading, accessLoading])

  const form = lang.marketplace.form

  useEffect(() => {
    if (isEdit) dispatch(fetchDeveloperApp({ id: initialAppId, version: initialVersion }))
  }, [initialAppId, initialVersion])

  useEffect(() => {
    if (accessLoading) return
    if (isEdit && hasPublicApiAccess && hadPublicApiAccess.current === false) {
      dispatch(fetchDeveloperApp({ id: initialAppId, version: initialVersion }))
    }
    hadPublicApiAccess.current = Boolean(hasPublicApiAccess)
  }, [hasPublicApiAccess, accessLoading])

  useEffect(() => {
    setNewScreenshots([])
    if (editApp.developerId) dispatch(fetchDeveloper(editApp.developerId))
  }, [editApp])

  useEffect(() => {
    setIsLoading(!!loadingMessage)
  }, [loadingMessage])

  useEffect(() => {
    return () => {
      dispatch(clearOnUnmount())
    }
  }, [])

  return (
    <div className={classes.root}>
      <div className={classes.loaderWrapper}>
        <Loader text={loadingMessage} visible className={classes.loader} />
      </div>
      <Formik
        initialValues={editApp}
        validationSchema={editValidationSchema}
        onSubmit={
          async({ appId, callbackUrl, name, summary, apiScopes, images, icon, description, reviewNotes, version, rejected, approved }) => {
            const { version: newVersion } = await dispatch(persistApp({
              appId,
              version,
              values: { callbackUrl, name, summary, apiScopes, description, reviewNotes, images, icon },
              icon: newIcon,
              screenshots: newScreenshots.map(s => s.file),
            },
            ))
            if (((rejected || approved) && newVersion !== initialVersion) || (!initialAppId && !initialVersion && isEdit)) {
              navigate(`/app/edit/${appId}/${newVersion}`)
            }
          }}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, handleSubmit, isSubmitting }) => {
          return (
            <Form className={clsx(classes.form, classes.main)}>
              <Topbar
                title={form.toolbar(initialAppId && initialVersion)}
                backButtonUrl={`apps/${PATH_PUBLISHED}`}
              >
                {!readOnly && (
                  <>
                    <CancelButton
                      id="cancel"
                      label={form.save(values.rejected || values.approved)}
                      type='submit'
                      disabled={values.review || ((values.rejected || values.approved) && !values.isLatestVersion) || isSubmitting}
                    />
                    <ButtonIconSquare
                      id="submit"
                      type="button"
                      className={classes.submit}
                      disabled={values.pending || values.review || values.rejected || values.approved || isSubmitting}
                      icon={IconCheck}
                      label={form.publish}
                      onClick={() => {
                        handleSubmit()
                        dispatch(publishApp({ id: values.appId, version: values.version }))
                      }}
                    />
                  </>
                )}
              </Topbar>

              {values.review && (
                <div className={classes.reviewHint}>{form.reviewHint}</div>
              )}
              <div className={clsx(classes.section, classes.firstSection)}>
                {readOnly
                  ? !accessLoading && <div className={classes.upsell}><FeatureLockedNotice featureType={FeatureTypes.PUBLIC_API} description={lang.marketplace.publicApi.textReadOnly} /></div>
                  : (
                    <>
                      <Title>{form.intro.headline}</Title>
                      <p dangerouslySetInnerHTML={{ __html: form.intro.text }} />
                      <TextInput disabled name="apiKey" variant="light" label={form.intro.inputs.key} className={classes.wideInput} useFormik enableCopy />
                      <TextInput disabled name="apiSecret" variant="light" label={form.intro.inputs.secret} className={classes.wideInput} useFormik type="password" enableReveal enableCopy />
                    </>
                  )}
                <div className={classes.callback}>
                  <TextInput name="callbackUrl" variant="light" label={form.intro.inputs.callback} className={classes.wideInput} useFormik disabled={values.review || readOnly} />
                  {!readOnly && <ButtonIconSquare label={lang.marketplace.form.testApp} onClick={() => window.open(values.appOpenUrl, '_blank')} />}
                </div>
              </div>

              <div className={classes.whiteBar}>
                <div className={classes.section}>
                  <div className={clsx(classes.splitWrapper, classes.appInfo)}>
                    <div className={classes.sectionFirst}>
                      <div>
                        <label>{form.info.name}</label>
                        <p>{values.name}</p>
                      </div>
                      <div>
                        <label>{form.info.version}</label>
                        <p>{values.version}</p>
                      </div>
                    </div>
                    <div className={classes.sectionSecond}>
                      <AppCard {...values} />
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.section}>
                <Title className={classes.headline}>{form.general.headline}</Title>
              </div>

              <div className={classes.section}>
                <div className={classes.splitWrapper}>
                  <div className={clsx(classes.sectionFirst, classes.box)}>
                    <Title size="small" className={classes.sectionHeadline}>{form.general.sections.info.headline}</Title>
                    <p dangerouslySetInnerHTML={{ __html: form.general.sections.info.text }} />
                    <TextInput
                      name="name"
                      className={classes.nameInput}
                      placeholder={form.general.sections.info.inputs.name(NAME_LENGTH)}
                      maxLength={NAME_LENGTH}
                      useFormik
                      disabled={values.review || readOnly}
                    />
                    <TextInput
                      name="summary"
                      placeholder={form.general.sections.info.inputs.summary(SUMMARY_LENGTH)}
                      maxLength={SUMMARY_LENGTH}
                      useFormik
                      disabled={values.review || readOnly}
                    />
                  </div>
                  <div className={clsx(classes.sectionSecond, classes.box)}>
                    <IconUpload
                      appIcon={values.icon}
                      setNewIcon={(file, base64) => {
                        setNewIcon(file)
                        setFieldValue('icon', base64)
                      }}
                      onDelete={() => setFieldValue('icon', '')}
                      disabled={values.review || readOnly}
                    />
                  </div>
                </div>
              </div>

              <div className={classes.section}>
                <Title className={classes.headline}>{form.details.headline}</Title>
              </div>

              <div className={classes.section}>
                <div className={clsx(classes.box)}>
                  <Screenshots
                    oldScreenshots={values.images}
                    setOldScreenshots={screenshots => setFieldValue('images', screenshots)}
                    newScreenshots={newScreenshots}
                    setNewScreenshots={setNewScreenshots}
                    disabled={values.review || readOnly}
                  />
                </div>
              </div>

              <div className={clsx(classes.section, classes.box)}>
                <Title size="small" className={classes.sectionHeadline}>{form.details.sections.description.headline}</Title>
                <p dangerouslySetInnerHTML={{ __html: form.details.sections.description.text }} />
                <HtmlInput
                  name="description"
                  wrapperClass={classes.descriptionText}
                  placeholder={form.details.sections.description.inputs.description}
                  useFormik
                  disabled={values.review || readOnly}
                  formats={['bold', 'italic', 'underline', 'bulletedList', 'numberedList', 'link', 'undo', 'redo']}
                />
              </div>

              <div className={clsx(classes.section, classes.box)}>
                <ScopeSection disabled={values.review || readOnly} />
              </div>

              <div className={clsx(classes.section, classes.box)}>
                <Title size="small" className={classes.sectionHeadline}>{form.details.sections.review.headline}</Title>
                <p dangerouslySetInnerHTML={{ __html: form.details.sections.review.text }} />
                <TextareaInput
                  name="reviewNotes"
                  className={classes.reviewText}
                  placeholder={form.details.sections.review.inputs.note(lang.productName)}
                  useFormik
                  disabled={values.review || readOnly}
                />
              </div>
            </Form>
          )
        }}
      </Formik>

      {!initialAppId && (
        <Formik
          initialValues={{
            name: '',
            callbackUrl: '',
          }}
          onSubmit={async({ name, callbackUrl }) => {
            try {
              await dispatch(createApp({ name, callbackUrl }))
              setIsEdit(true)
            } catch (e) {
              if (isPublicApiNotBookedError(e)) return
              const { body: { meta: { code: [{ code: errorCode }] } } } = e
              toast.showToast({ type: 'error', message: lang.marketplace.errors[errorCode] ?? lang.defaultErrorMessage })
            }
          }}
          enableReinitialize={true}
          validationSchema={keyValidationSchema}
        >
          <Form className={clsx(classes.form, classes.intro)}>
            <Topbar
              className={classes.topbar}
              title={form.toolbar(initialAppId && initialVersion)}
              backButtonUrl={'/apps'}
            />

            <div className={clsx(classes.section, classes.firstSection)}>
              <Title>{form.create.headline}</Title>
              <p className={classes.introText} dangerouslySetInnerHTML={{ __html: form.create.text }} />

              <TextInput
                name="name"
                className={classes.wideInput}
                placeholder={form.general.sections.info.inputs.name(NAME_LENGTH)}
                maxLength={NAME_LENGTH}
                useFormik
              />
              <TextInput
                name="callbackUrl"
                className={classes.wideInput}
                placeholder={form.intro.inputs.callback}
                useFormik
              />

              <div className={classes.buttons}>
                <ButtonIconSquare type="submit" label={form.create.inputs.button} className={classes.submit} icon={IconCheck} />
              </div>
            </div>
          </Form>
        </Formik>
      )}
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  root: ({ isLoading, isEdit }) => ({
    position: 'relative',
    backgroundColor: theme.backgroundColor,
    height: `calc(100vh - ${theme.menuHeight}px)`,
    overflow: isLoading || !isEdit ? 'hidden' : 'auto',
  }),
  form: {
    margin: 0,
    transition: 'opacity .5s',
  },
  submit: {
    marginLeft: '1rem',
  },
  section: {
    width: 925,
    margin: '0 auto 40px',
  },
  firstSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 47,
  },
  sectionFirst: {
    width: 500,
    display: 'flex',
    flexDirection: 'column',
    '& label': {
      fontSize: 12,
      lineHeight: '26px',
      textTransform: 'uppercase',
      color: theme.darkGrey,
      '& + p': {
        fontSize: 20,
        lineHeight: '26px',
        margin: 0,
      },
    },
    '& > div': {
      marginBottom: 20,
    },
  },
  sectionSecond: {
    width: 385,
    display: 'flex',
    justifyContent: 'center',
  },
  box: {
    background: '#fff',
    padding: 40,
  },
  splitWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  headline: {
    marginBottom: 0,
  },
  sectionHeadline: {
    marginBottom: 20,
  },
  nameInput: {
    marginBottom: 40,
  },
  descriptionText: {
    height: 189,
  },
  reviewText: {
    height: 248,
  },
  intro: ({ isEdit }) => ({
    width: '100%',
    position: 'absolute',
    top: 0,
    '& p': {
      marginTop: 0,
    },
    opacity: isEdit ? 0 : 1,
    zIndex: isEdit ? 0 : 1,
  }),
  main: ({ isEdit }) => ({
    opacity: isEdit ? 1 : 0,
    '& p': {
      marginTop: 0,
    },
    zIndex: isEdit ? 1 : 0,
    position: 'relative',
  }),
  wideInput: {
    maxWidth: 600,
    marginBottom: 10,
  },
  buttons: {
    width: '100%',
    maxWidth: 600,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  whiteBar: {
    background: '#fff',
  },
  appInfo: {
    padding: '20px 0',
  },
  reviewHint: {
    padding: 10,
    textAlign: 'center',
    backgroundColor: theme.primary,
    color: '#fff',
  },
  loaderWrapper: ({ isLoading }) => ({
    display: isLoading ? 'block' : 'none',
    position: 'sticky',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 16,
  }),
  loader: {
    opacity: 0.5,
  },
  upsell: {
    width: '100%',
    maxWidth: 600,
  },
  introText: {
    textAlign: 'center',
  },
  callback: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 600,
    alignItems: 'flex-end',
    '& $wideInput': {
      marginBottom: 0,
      marginRight: 30,
    },
  },
}))

AppForm.propTypes = {
  initialAppId: PropTypes.string,
  initialVersion: PropTypes.string,
}

AppForm.defaultProps = {
  initialAppId: null,
  initialVersion: null,
}

export default AppForm
